import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { DEFAULT_LANG, privacyPathForLang } from "@/lib/seo";
import { PRICE_LABEL, LIST_PRICE_LABEL, DISCOUNT_LABEL } from "@/lib/pricing";
import {
  Eye, Check, Contrast, ScanEye, Grid3x3, Palette, Blend, Radar,
  ArrowRight, Lock, Building2, Gift,
} from "lucide-react";

// Self-hosted on purpose. Hotlinking this from images.unsplash.com sent every visitor's IP
// to a US provider on page load, before any interaction and with nothing to base it on —
// the Google Fonts fact pattern. Disclosing it in the privacy policy is not a legal basis;
// serving the bytes ourselves removes the transfer entirely.
const HERO_IMG = "/hero-vision-test.jpg";

export default function Landing({ lang }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, lang: current, setLang } = useI18n();

  // The URL already decided the language (see detectLang), so this only carries that
  // choice into the app the visitor is about to enter. The root is skipped on purpose:
  // it is the x-default, and overwriting a returning user's saved language just because
  // they typed the bare domain would be a regression, not a preference.
  React.useEffect(() => {
    if (lang && lang !== DEFAULT_LANG && lang !== current) setLang(lang);
  }, [lang, current, setLang]);

  const TESTS = [
    { icon: Eye, t: "lp_t1_t", d: "lp_t1_d" }, { icon: Contrast, t: "lp_t2_t", d: "lp_t2_d" },
    { icon: ScanEye, t: "lp_t3_t", d: "lp_t3_d" }, { icon: Grid3x3, t: "lp_t4_t", d: "lp_t4_d" },
    { icon: Palette, t: "lp_t5_t", d: "lp_t5_d" }, { icon: Blend, t: "lp_t6_t", d: "lp_t6_d" },
    { icon: Radar, t: "lp_t7_t", d: "lp_t7_d" },
  ];
  const STEPS = [
    { n: "1", t: "lp_s1_t", d: "lp_s1_d" }, { n: "2", t: "lp_s2_t", d: "lp_s2_d" },
    { n: "3", t: "lp_s3_t", d: "lp_s3_d" }, { n: "4", t: "lp_s4_t", d: "lp_s4_d" },
  ];

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-900" data-testid="landing-page">
      <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/90 backdrop-blur">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <span className="w-9 h-9 rounded-md bg-primary text-white flex items-center justify-center">
              <Eye className="w-5 h-5" strokeWidth={2} />
            </span>
            <span className="leading-tight">
              <span className="block text-base font-bold tracking-tight">VisioScreening</span>
              <span className="block text-[11px] font-medium uppercase tracking-widest text-slate-400">{t("brand_sub")}</span>
            </span>
          </div>
          <div className="flex items-center gap-2">
            <LanguageSwitcher className="hidden sm:flex mr-1" />
            {user ? (
              <Button onClick={() => navigate("/app")} data-testid="landing-goto-app" className="bg-primary hover:bg-primary/90">
                {t("nav_goto_app")} <ArrowRight className="w-4 h-4" />
              </Button>
            ) : (
              <>
                <Button variant="ghost" onClick={() => navigate("/login")} data-testid="landing-login-btn">{t("nav_login")}</Button>
                <Button onClick={() => navigate("/register")} data-testid="landing-register-btn" className="bg-primary hover:bg-primary/90">
                  {t("nav_register")}
                </Button>
              </>
            )}
          </div>
        </div>
      </header>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-14 sm:py-20 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <span className="inline-flex items-center gap-2 rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-emerald-700" data-testid="hero-free-badge">
            <Gift className="w-4 h-4" /> {t("lp_free_badge")}
          </span>
          <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter leading-[1.05]">
            {t("lp_hero_1")} <span className="text-primary">{t("lp_hero_accent")}</span>
          </h1>
          <p className="mt-5 text-lg text-slate-600 leading-relaxed max-w-xl">{t("lp_hero_desc")}</p>
          <div className="mt-8">
            <Button size="lg" onClick={() => navigate(user ? "/app" : "/register")} data-testid="hero-cta-btn"
              className="h-12 px-7 text-base font-semibold bg-primary hover:bg-primary/90">
              <Gift className="w-5 h-5" /> {t("lp_cta")}
            </Button>
            <p className="mt-3 text-sm text-slate-500" data-testid="hero-free-reassure">{t("lp_free_reassure")}</p>
          </div>
          <ul className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-600">
            <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-600" /> {t("lp_feat_secure")}</li>
            <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-600" /> {t("lp_feat_isolated")}</li>
            <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-600" /> {t("lp_feat_unlimited")}</li>
          </ul>
        </div>
        <div className="relative">
          <div className="absolute -inset-4 bg-primary/10 rounded-3xl -rotate-2" />
          <img src={HERO_IMG} alt="Vision test" className="relative rounded-2xl shadow-xl w-full h-[380px] object-cover" data-testid="hero-image" />
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
        <h2 className="text-lg font-bold uppercase tracking-widest text-primary">{t("lp_inc_kicker")}</h2>
        <p className="mt-2 text-3xl sm:text-4xl font-extrabold tracking-tight">{t("lp_inc_title")}</p>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {TESTS.map((x, i) => (
            <div key={i} className="rounded-xl border border-slate-200 bg-white p-6 hover:shadow-md transition-shadow" data-testid={`test-card-${i}`}>
              <div className="w-11 h-11 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                <x.icon className="w-6 h-6" strokeWidth={1.8} />
              </div>
              <h3 className="mt-4 text-base font-bold">{t(x.t)}</h3>
              <p className="mt-1.5 text-sm text-slate-500 leading-relaxed">{t(x.d)}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
        <div className="rounded-2xl bg-slate-900 text-white p-8 sm:p-12">
          <h2 className="text-lg font-bold uppercase tracking-widest text-sky-300">{t("lp_how_kicker")}</h2>
          <p className="mt-2 text-3xl font-extrabold tracking-tight">{t("lp_how_title")}</p>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {STEPS.map((s) => (
              <div key={s.n}>
                <div className="w-10 h-10 rounded-full bg-sky-500 flex items-center justify-center font-bold text-lg">{s.n}</div>
                <h3 className="mt-4 text-base font-bold">{t(s.t)}</h3>
                <p className="mt-1.5 text-sm text-slate-300 leading-relaxed">{t(s.d)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 sm:px-6 py-12" data-testid="pricing-section">
        <p className="text-center text-slate-600 max-w-xl mx-auto">{t("lp_pricing_desc")}</p>
        <div className="mt-8 grid sm:grid-cols-2 gap-5 items-stretch">
          {/* Colonna gratuita per prima: è l'offerta d'ingresso, non un contorno del prezzo. */}
          <div className="flex flex-col rounded-2xl border-2 border-emerald-200 bg-white p-8 shadow-sm" data-testid="pricing-free-card">
            <span className="inline-flex items-center gap-2 self-start rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-emerald-700">
              <Gift className="w-4 h-4" /> {t("lp_free_col_title")}
            </span>
            <p className="mt-5 text-5xl font-extrabold tracking-tighter text-emerald-700">{t("lp_free_col_price")}</p>
            <ul className="mt-6 space-y-2.5 flex-1">
              {["lp_free_f1", "lp_free_f2", "lp_free_f3"].map((k, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-slate-700">
                  <span className="mt-0.5 w-5 h-5 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center flex-shrink-0">
                    <Check className="w-3.5 h-3.5" strokeWidth={2.5} />
                  </span>{t(k)}
                </li>
              ))}
            </ul>
            <Button size="lg" onClick={() => navigate(user ? "/app" : "/register")} data-testid="pricing-cta-btn"
              className="mt-8 h-12 w-full text-base font-semibold bg-primary hover:bg-primary/90">
              {t("lp_free_col_cta")}
            </Button>
          </div>

          <div className="flex flex-col rounded-2xl border border-slate-200 bg-white p-8 shadow-sm" data-testid="pricing-paid-card">
            <span className="inline-flex items-center gap-2 self-start rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-primary">
              <Lock className="w-4 h-4" /> {t("lp_paid_col_title")}
            </span>
            <div className="mt-5 flex items-end gap-2 flex-wrap">
              <span className="text-5xl font-extrabold tracking-tighter" data-testid="pricing-paid-price">{PRICE_LABEL}</span>
              {LIST_PRICE_LABEL && (
                <s className="mb-1.5 text-lg font-semibold text-slate-400" data-testid="pricing-paid-list-price">{LIST_PRICE_LABEL}</s>
              )}
              {DISCOUNT_LABEL && (
                <span className="mb-2 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-bold text-emerald-700">{DISCOUNT_LABEL}</span>
              )}
              <span className="mb-1.5 text-sm text-slate-400 font-medium">{t("lp_price_once")}</span>
            </div>
            <ul className="mt-6 space-y-2.5 flex-1">
              {["lp_paid_f1", "lp_paid_f2", "lp_paid_f3"].map((k, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-slate-700">
                  <span className="mt-0.5 w-5 h-5 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center flex-shrink-0">
                    <Check className="w-3.5 h-3.5" strokeWidth={2.5} />
                  </span>{t(k)}
                </li>
              ))}
            </ul>
            <p className="mt-8 rounded-md bg-slate-50 px-4 py-3 text-sm text-slate-500">{t("lp_paid_note")}</p>
          </div>
        </div>
        <p className="mt-6 text-center text-xs text-slate-400">
          {t("lp_have_account")} <Link to="/login" className="text-primary font-semibold underline">{t("nav_login")}</Link>
        </p>
      </section>

      <footer className="border-t border-slate-200 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-slate-400">
          <div className="flex items-center gap-2"><Building2 className="w-4 h-4" /> {t("lp_footer_tag")}</div>
          <span>{t("lp_footer_secure")}</span>
          {/* A real anchor, not a <Link>: the policy is prerendered per language just like
              this page, so the crawler must be able to walk to it without running React. */}
          <a href={privacyPathForLang(lang || current)} className="transition-colors hover:text-slate-900"
            data-testid="landing-privacy-link">{t("nav_privacy")}</a>
          {/* Repeated here because the header copy is hidden on small screens. */}
          <LanguageSwitcher />
        </div>
      </footer>
    </div>
  );
}
